/**
 * Generates a stable background color for a cloud resource type.
 *
 * The color is derived from a hash of the normalized type name, so the same
 * type always maps to the same color across accounts, services and requests.
 * The returned color is a pastel shade readable with dark text, suitable as a
 * chip background.
 */
export function getResourceTypeColor(resourceType: string): string {
  const normalized = resourceType.trim().toLowerCase();
  const hash = fnv1a32(normalized);
  const hue = hash % 360;
  // Vary saturation/lightness slightly with the hash so types with close hues
  // remain distinguishable.
  const saturation = 0.5 + ((hash >> 8) % 4) * 0.05;
  const lightness = 0.8 + ((hash >> 12) % 3) * 0.03;
  return hslToHex(hue, saturation, lightness);
}

function fnv1a32(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const channel = (offset: number) => {
    const k = (offset + hue / 30) % 12;
    const a = saturation * Math.min(lightness, 1 - lightness);
    const value = lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(value * 255);
  };
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(channel(0))}${toHex(channel(8))}${toHex(channel(4))}`;
}
